import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sun, Moon, Languages, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useLanguage } from "@/i18n/LanguageContext";

const NAV_ITEMS = [
  { key: "services", href: "#services" },
  { key: "products", href: "#products" },
  { key: "about", href: "#about" },
  { key: "contact", href: "#contact" },
];

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const location = useLocation();
  const prefix = location.pathname === "/" ? "" : "/";
  const [dark, setDark] = useState(() => localStorage.getItem("theme") !== "light");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const renderLinks = (onClick) => (
    <>
      {NAV_ITEMS.map((item) => (
        <a
          key={item.key}
          data-testid={`nav-link-${item.key}`}
          href={`${prefix}${item.href}`}
          onClick={onClick}
          className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {t.nav[item.key]}
        </a>
      ))}
      <Link
        to="/careers"
        data-testid="nav-link-careers"
        onClick={onClick}
        className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        {t.nav.careers}
      </Link>
      <Link
        to="/exams"
        data-testid="nav-link-exam"
        onClick={onClick}
        className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        {t.nav.exam}
      </Link>
      <Link
        to="/account"
        data-testid="nav-link-account"
        onClick={onClick}
        className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        {t.nav.account}
      </Link>
    </>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <a href={`${prefix}#top`} data-testid="navbar-brand-logo" className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Aadisarvtra logo" className="h-10 w-10 rounded-lg object-cover" />
          <span className="leading-tight">
            <span className="block font-heading text-base font-bold tracking-tight">{t.brand}</span>
            <span className="block text-[10px] uppercase tracking-widest text-muted-foreground">
              {t.brandSub}
            </span>
          </span>
        </a>
        <nav className="hidden items-center gap-7 lg:flex">{renderLinks()}</nav>
        <div className="flex items-center gap-2">
          <Button
            data-testid="language-toggle-button"
            variant="ghost"
            size="sm"
            onClick={() => setLang(lang === "en" ? "hi" : "en")}
            className="gap-1.5 font-semibold"
          >
            <Languages className="h-4 w-4" />
            {lang === "en" ? "हिंदी" : "EN"}
          </Button>
          <Button
            data-testid="theme-toggle-button"
            variant="ghost"
            size="icon"
            onClick={() => setDark(!dark)}
            aria-label="Toggle theme"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button asChild data-testid="nav-enquire-button" className="hidden sm:inline-flex">
            <a href={`${prefix}#contact`}>{t.nav.enquire}</a>
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                data-testid="mobile-menu-button"
                variant="ghost"
                size="icon"
                className="lg:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="flex flex-col gap-6 pt-12">
              {renderLinks(() => setOpen(false))}
              <Button asChild onClick={() => setOpen(false)}>
                <a href="#contact">{t.nav.enquire}</a>
              </Button>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
